/**
 * @file Component.js
 * @description A versatile, multi-purpose container component that displays various types of content based on ContentTypeID. This component supports different content types including documents, multimedia, child portals, links, asset uploads, and tables. It provides features like content acknowledgment, help text display, navigation between portal pages, SSO authentication for links, and supports both view and edit modes.
 *
 * The component handles:
 * - Different visual presentations based on content type
 * - Required acknowledgments with tracking
 * - Help text visibility and expansion
 * - Navigation to child portals and external links
 * - SSO authentication flows for protected resources
 * - Responsive layout and dimensions
 * - Edit mode with resize and context menu capabilities
 * - Asset upload status tracking
 *
 * @component
 */

import { checkPortalAccess } from '../utils/portalAccessUtil';

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../components/charts/Charts.css';
import '../components/Component.css';
import { ContentType } from '../components/ContentType';
import ContainerContextMenu from './new-portal-page/ContainerContextMenu';
import { hasText, isValidUrl } from '../utils/utils';
import {
    closePendingWindow,
    navigatePendingWindow,
    openPendingWindow,
} from '../utils/pendingWindow';
import {
    ContainerTypes,
    EventDataTypeEnum,
    LogEventType,
    SecurityAttributes,
    UserType,
} from '../utils/data/enums';
import { useIndividualHasBenefitAccess } from '../hooks/useIndividualHasBenefitAccess';
import { useAuthContext } from '../store/AuthContext';
import {
    createConversation,
    createMessage,
    getAllConversations,
    getAllMessages,
} from '../services/conversationService';
import {
    fetchClientSSOById,
    fetchSystemSSOById,
    loginClientSSO,
} from '../services/ssoService';
import Toast from './ui/toasts/Toast';
import { logEvent } from '../services/loggingService';
import { ROUTE_BUILDERS, ROUTES } from '../constants/routes';
import {
    handleSSOLoginFailedLinkClick,
    showSSOGenericError,
    showSSOLoginValidationWarning,
} from '../utils/ssoLinkClickHandler';
import { getText } from '../i18n';
export function Component({
    ID,
    Name,
    Subtitle,
    ContentTypeID,
    HelpBoxText,
    AcknowledgmentReq = 1,
    AcknowledgmentText,
    Acknowledged = false,
    editMode,
    width,
    height,
    dueDate,
    data,
    navigationStack,
    displayTitle,
    isMobile = false,
    contextMenuRef,
    activeBlock,
    block,
    contentDivRefs,
    handleResizeMouseDown,
    handlePropertiesClick,
    handleDeleteClick,
    toggleContextMenu,
    portalName,
    portalId,
    onShowPortalView,
    isPreview = false,
    suppressAcknowledgment = false,
    isViewLive = false,
}) {
    const [showHelpBox, setShowHelpBox] = useState(false);
    const [expandHelpBox, setExpandHelpBox] = useState(false);
    const [ack, setAck] = useState(Acknowledged);
    const [conversationId, setConversationId] = useState(0);

    const { user, hasSecurityAttribute } = useAuthContext();
    const isLimitedUser = user?.jwt?.type === UserType.LimitedUser;
    const showIndividualBenefits = useIndividualHasBenefitAccess();
    const [backgroundColor, setBackgroundColor] = useState(null);
    const [, setForegroundColor] = useState(null);
    const [isLinkContainerWithImage, setIsLinkContainerWithImage] =
        useState(false);
    const [isChildPortalWithIcon, setIsChildPortalWithIcon] = useState(false);
    const [isChildPortalWithImage, setIsChildPortalWithImage] = useState(false);

    const [daysSinceOrToDueDate, setDaysSinceOrToDueDate] = useState(0);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();
    const location = useLocation();

    // Track window resize for mobile detection
    useEffect(() => {
        const handleResize = () => {
            setIsMobileView(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // TODO: Delete if not useful
    const handleSetBackgroundColor = color => {
        setBackgroundColor(color);
    };

    useEffect(() => {
        if (isPreview) {
            return;
        }

        if (
            AcknowledgmentReq === 1 ||
            data.type === ContainerTypes.AssetUpload
        ) {
            getAllConversations('container', data.id)
                .then(conversations => {
                    let existingConversationId = conversations[0]?.id;
                    if (!existingConversationId) {
                        if (isViewLive) {
                            return;
                        }
                        const newConversation = {
                            topicId: data.id,
                            topicType: 'container',
                        };
                        createConversation(newConversation)
                            .then(conversation => {
                                existingConversationId = conversation[0].id;
                            })
                            .catch(error => {
                                console.error(
                                    'createConversation failed:',
                                    error,
                                );
                            });
                    }
                    setConversationId(existingConversationId);

                    getAllMessages(existingConversationId, user.jwt.nameid)
                        .then(messages => {
                            if (messages?.length === 0) setAck(false);
                            else {
                                const latestMessage = messages.reduce(
                                    function (prev, current) {
                                        return prev && prev.id > current.id
                                            ? prev
                                            : current;
                                    },
                                );
                                setAck(
                                    latestMessage.contents === 'false' || null
                                        ? false
                                        : true,
                                );
                            }
                        })
                        .catch(error => {
                            console.error('createConversation failed:', error);
                        });
                })
                .catch(error => {
                    console.error('getAllConversations failed:', error);
                });
        }
    }, []);

    useEffect(() => {
        if (dueDate) {
            setDaysSinceOrToDueDate(calculateDaysSinceDate(dueDate));
        }
    }, [dueDate]);

    const calculateDaysSinceDate = dateString => {
        const inputDate = new Date(dateString);
        const currentDate = new Date();
        const differenceInMilliseconds = currentDate - inputDate;
        const millisecondsInADay = 1000 * 60 * 60 * 24;
        return Math.floor(differenceInMilliseconds / millisecondsInADay);
    };

    useEffect(() => {
        // TODO: When data is updated, review any relevant parts and trigger a Container re-render accordingly
        if (data) {
            if (
                data.type === ContainerTypes.ChildPortal &&
                data.childPortalPart !== null
            ) {
                if (data.childPortalPart.glyph !== null) {
                    setBackgroundColor(data.childPortalPart.backgroundColor);
                    setForegroundColor(data.childPortalPart.foregroundColor);
                    setIsChildPortalWithIcon(true);
                } else if (data.childPortalPart.image !== null) {
                    setIsChildPortalWithImage(true);
                }
            } else if (
                data.type === ContainerTypes.Link &&
                data.linkPart !== null
            ) {
                if (data.linkPart.glyph) {
                    setBackgroundColor(data.linkPart.backgroundColor);
                    setForegroundColor(data.linkPart.foregroundColor);
                    setIsChildPortalWithIcon(true);
                } else if (data.linkPart.image) {
                    setIsChildPortalWithImage(true);
                    setIsLinkContainerWithImage(true);
                }
            }
        }
    }, [data]);

    const navigateToChildPortal = async childPortalId => {
        try {
            await checkPortalAccess({
                portalId: childPortalId,
                isViewLive,
            });

            const baseStack =
                Array.isArray(navigationStack) && navigationStack.length
                    ? navigationStack.slice()
                    : portalId
                      ? [portalId]
                      : [];
            const homeId =
                baseStack.length > 0 ? baseStack[0] : portalId || null;

            if (
                (location.pathname === '/' || location.pathname === '') &&
                homeId !== null
            ) {
                const newHomeState = {
                    ...(location.state || {}),
                    selectedTabId: homeId,
                };
                navigate(location.pathname || '/', {
                    replace: true,
                    state: newHomeState,
                });
            }

            const newBack = [...baseStack, childPortalId];
            try {
                sessionStorage.setItem(
                    'navigationStack:back',
                    JSON.stringify(newBack),
                );
            } catch (e) {
                console.warn('Failed to set navigationStack:back', e);
            }

            if (isPreview && typeof onShowPortalView === 'function') {
                onShowPortalView(childPortalId, newBack);
                return;
            }

            const route = isViewLive
                ? ROUTE_BUILDERS.viewLivePortal(childPortalId)
                : ROUTE_BUILDERS.portalPagesView(childPortalId);

            navigate(route, {
                state: {
                    navigationStack: newBack,
                },
            });
        } catch (error) {
            if (error?.code === 'NO_PORTAL_ACCESS') {
                Toast.info(
                    getText('msg_b8c3f507d91a'),
                    {},
                    getText('msg_13d4498a0dcc'),
                );
                return;
            }

            console.error('navigateToChildPortal failed:', error);
            Toast.error(getText('msg_fbb9c884f239'));
        }
    };

    const navigateToLink = async () => {
        if (
            (editMode || isPreview || isViewLive) &&
            data?.linkPart?.tenantSSOId
        ) {
            Toast.info(
                getText('msg_29393dec2c1f'),
                {},
                getText('msg_6c8145e3a0f9'),
            );
            return;
        }

        if (isPreview || isViewLive) {
            const menuRoutes = [
                ROUTES.MY_MESSAGES,
                ROUTES.MY_BENEFITS,
                ROUTES.MY_ASSETS,
            ];
            if (menuRoutes.includes(data.linkPart.url)) {
                Toast.info(getText('msg_7ebb77bbfd9c'));
                return;
            }
        }

        if (data.linkPart.tenantSSOId !== null) {
            const pendingWindow = openPendingWindow();
            try {
                let ssoData = await fetchClientSSOById(
                    data.linkPart.tenantSSOId,
                );
                if (!hasText(ssoData?.url)) {
                    ssoData = await fetchSystemSSOById(ssoData.ssoId);
                }
                const loginResponse = await loginClientSSO(ssoData);
                const loginUrl = loginResponse?.login?.saml20?.url;
                if (!isValidUrl(loginUrl)) {
                    closePendingWindow(pendingWindow);
                    showSSOLoginValidationWarning();
                    return;
                }

                navigatePendingWindow(pendingWindow, loginUrl);
            } catch (error) {
                closePendingWindow(pendingWindow);
                console.error('Error attempting SSO:', error);
                showSSOGenericError();
                return;
            }
        } else {
            const url = data.linkPart.url;
            if (url === ROUTES.MY_MESSAGES) {
                if (isLimitedUser) {
                    Toast.error(getText('msg_4cf125274798'));
                    return;
                }
                navigate(url);
            } else if (url === ROUTES.MY_BENEFITS) {
                if (
                    !showIndividualBenefits ||
                    (isLimitedUser &&
                        !hasSecurityAttribute([
                            SecurityAttributes.UserAccessBenefits,
                        ]))
                ) {
                    Toast.error(getText('msg_b635a89257ec'));
                    return;
                }
                navigate(url);
            } else if (url === ROUTES.MY_ASSETS) {
                navigate(url);
            } else {
                window.open(url, '_blank');
            }
        }

        if (!isViewLive) {
            logEvent(
                LogEventType.UserLinkClicked,
                `Visited link ${data.linkPart.url}`,
                {
                    contextDataTypeId: EventDataTypeEnum.Portal,
                    contextId: portalId,
                    contextName: portalName || undefined,
                },
            );
        }
    };

    const navigateToMultimediaLink = async () => {
        if (
            (editMode || isPreview || isViewLive) &&
            data?.multimediaPart?.tenantSSOId
        ) {
            Toast.info(
                getText('msg_29393dec2c1f'),
                {},
                getText('msg_6c8145e3a0f9'),
            );
            return;
        }

        if (data.multimediaPart.tenantSSOId !== null) {
            const pendingWindow = openPendingWindow();
            try {
                let ssoData = await fetchClientSSOById(
                    data.multimediaPart.tenantSSOId,
                );
                if (!hasText(ssoData?.url)) {
                    ssoData = await fetchSystemSSOById(ssoData.ssoId);
                }
                const loginResponse = await loginClientSSO(ssoData);
                const loginUrl = loginResponse?.login?.saml20?.url;
                if (!isValidUrl(loginUrl)) {
                    closePendingWindow(pendingWindow);
                    showSSOLoginValidationWarning();
                    return;
                }

                navigatePendingWindow(pendingWindow, loginUrl);
            } catch (error) {
                closePendingWindow(pendingWindow);
                console.error('Error attempting SSO:', error);
                showSSOGenericError();
                return;
            }
        } else {
            window.open(data.multimediaPart.url, '_blank');
        }

        if (!isViewLive) {
            logEvent(
                LogEventType.UserLinkClicked,
                `Visited link ${data.multimediaPart.url}`,
                {
                    contextDataTypeId: EventDataTypeEnum.Portal,
                    contextId: portalId,
                    contextName: portalName || undefined,
                },
            );
        }
    };

    const handleContainerClick = () => {
        if (ContentTypeID === ContainerTypes.ChildPortal) {
            navigateToChildPortal(data.childPortalPart.childPortalId);
        } else if (
            ContentTypeID === ContainerTypes.Link &&
            data.linkPart !== null
        ) {
            if (data.linkPart.childPortalId !== null) {
                navigateToChildPortal(data.linkPart.childPortalId);
            } else {
                navigateToLink();
            }
        } else if (
            ContentTypeID === ContainerTypes.Multimedia &&
            data.multimediaPart !== null
        ) {
            if (data.multimediaPart.childPortalId !== null) {
                navigateToChildPortal(data.multimediaPart.childPortalId);
            } else {
                navigateToMultimediaLink();
            }
        }
    };

    const formatDueDate = isoDate => {
        // TODO: Delete appended ' isoDate!=="0001-01-01T00:00:00" 'else if condition once the backend can store null values for lack of due date
        let formattedDate = '';
        if (
            isoDate !== null &&
            isoDate !== undefined &&
            isoDate !== '0001-01-01T00:00:00'
        ) {
            const date = new Date(isoDate);
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            const year = date.getFullYear().toString();
            formattedDate = `${month}/${day}/${year}`;
        }
        return formattedDate;
    };

    const dateText = formatDueDate(dueDate);

    const unacknowledgedHeaderText =
        'Acknowledgment must be completed' +
        (dateText !== '' ? ' by ' + dateText : '');
    const acknowledgedHeaderText = getText('msg_7d0ca4d27b42');
    const assetUploadIncompleteHeaderText =
        'Must be completed' + (dateText !== '' ? ' by ' + dateText : '');
    const assetUploadCompleteHeaderText = getText('msg_1798b3ba42ee');
    const assetUploadPastDueHeaderText = getText('msg_7c15f3b797eb');

    const onAckChange = checked => {
        if (isPreview || isViewLive) {
            setAck(checked);
            if (checked) setExpandHelpBox(false);
            return;
        }

        const newMessage = {
            conversationId: conversationId,
            contents: checked.toString(),
        };

        createMessage(newMessage)
            .then(() => {
                setAck(checked);
            })
            .catch(error => {
                console.error('createMessage failed:', error);
            });
        if (checked) setExpandHelpBox(false);
    };

    const [, setHasScrollbar] = useState(false);

    const contentRef = React.useRef(null);
    const hasVerticalScrollbar = ref => {
        if (!ref.current) return false;
        return ref.current.scrollHeight > ref.current.clientHeight;
    };

    useEffect(() => {
        const scrollbarPresence = hasVerticalScrollbar(contentRef);
        setHasScrollbar(scrollbarPresence);
    }, [width, height]);

    const multimediaPart = data?.multimediaPart;
    const hasMultimediaImages =
        multimediaPart?.images &&
        Array.isArray(multimediaPart?.images) &&
        multimediaPart.images.length > 0;
    const hasMultimediaVideo =
        multimediaPart?.video &&
        multimediaPart?.video !== null &&
        multimediaPart?.video !== '';
    const hasMultimediaMedia = hasMultimediaImages || hasMultimediaVideo;
    const hasMultimediaText =
        multimediaPart?.text &&
        multimediaPart?.text !== '' &&
        multimediaPart?.text !== null &&
        multimediaPart?.text !== '<div><br></div>' &&
        multimediaPart?.text !== '<div></div>';
    const hasMultimediaButton =
        multimediaPart?.buttonText &&
        multimediaPart?.buttonText !== '' &&
        multimediaPart?.buttonText !== null;
    const hasMultimediaTextOrButton = hasMultimediaText || hasMultimediaButton;

    const isMediaOnlyMultimedia =
        ContentTypeID === ContainerTypes.Multimedia &&
        hasMultimediaMedia &&
        !hasMultimediaTextOrButton &&
        !displayTitle;

    const linkPart = data?.linkPart;
    const hasLinkMedia =
        linkPart?.image !== null && linkPart?.image !== undefined;
    const hasLinkText =
        linkPart?.text &&
        linkPart?.text !== '' &&
        linkPart?.text !== '<div><br></div>' &&
        linkPart?.text !== '<div></div>';
    const hasLinkButton =
        linkPart?.buttonText &&
        linkPart?.buttonText !== '' &&
        linkPart?.buttonText !== null;
    const isImageOnlyLink =
        ContentTypeID === ContainerTypes.Link &&
        hasLinkMedia &&
        !linkPart?.glyph &&
        !hasLinkText &&
        !hasLinkButton &&
        !displayTitle;

    const isMultimediaMediaWithTitle =
        ContentTypeID === ContainerTypes.Multimedia &&
        hasMultimediaMedia &&
        !hasMultimediaTextOrButton &&
        displayTitle;

    const isLinkImageWithTitle =
        ContentTypeID === ContainerTypes.Link &&
        hasLinkMedia &&
        !linkPart?.glyph &&
        !hasLinkText &&
        !hasLinkButton &&
        displayTitle;

    const isMediaOnlyContent = isMediaOnlyMultimedia || isImageOnlyLink;
    const isImageFillContent = isMediaOnlyContent;

    const containerAssets = data?.containerAssets;
    const hasAssetUploadVideo =
        data?.assetUploadPart?.video !== null &&
        data?.assetUploadPart?.video !== undefined &&
        data?.assetUploadPart?.video !== '';
    const hasAssetUploadMedia =
        (Array.isArray(containerAssets) && containerAssets.length > 0) ||
        hasAssetUploadVideo;

    const subtitleMarginByType = {
        [ContainerTypes.Multimedia]: hasMultimediaMedia,
        [ContainerTypes.Link]: hasLinkMedia,
        [ContainerTypes.AssetUpload]: hasAssetUploadMedia,
        [ContainerTypes.Table]: false,
    };
    const shouldSubtitleHaveMargin =
        subtitleMarginByType[ContentTypeID] ?? true;

    const getPaddingContainer = () => {
        const ACK_TOP_PADDING_WIDE_SPACED = 56;
        const ACK_TOP_PADDING_NARROW_SPACED = 84;
        const ACK_TOP_PADDING_WIDE_FLUSH = 36;
        const ACK_TOP_PADDING_NARROW_FLUSH = 64;

        const containerWidth = width || 0;
        const hasDueDate =
            dueDate !== null &&
            dueDate !== undefined &&
            dueDate !== '' &&
            dueDate !== '0001-01-01T00:00:00';
        const needsAdditionalPadding =
            (containerWidth < 371 && hasDueDate) || containerWidth < 250;

        switch (true) {
            // Link or Child container in "Icon" view
            case isChildPortalWithIcon:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED - 20
                        : ACK_TOP_PADDING_WIDE_SPACED - 20;
                    return `${top}px 20px 0px`;
                }
                return '0px 20px';

            // Document container
            case ContentTypeID === ContainerTypes.Document:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 20px 20px 20px`;
                }
                return '20px';

            // Table container
            case ContentTypeID === ContainerTypes.Table:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 20px 20px 20px`;
                }
                return '20px';

            // Multimedia
            // 5) no media, text/button (with or without title)
            case ContentTypeID === ContainerTypes.Multimedia &&
                !hasMultimediaMedia &&
                hasMultimediaTextOrButton:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 20px 20px 20px`;
                }
                return '20px';
            // 1) media only, no title, no text/button
            case ContentTypeID === ContainerTypes.Multimedia &&
                hasMultimediaMedia &&
                !displayTitle &&
                !hasMultimediaTextOrButton:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_FLUSH
                        : ACK_TOP_PADDING_WIDE_FLUSH;
                    return `${top}px 0px 0px 0px`;
                }
                return '0px';
            // 2) media + text/button, no title
            case ContentTypeID === ContainerTypes.Multimedia &&
                hasMultimediaMedia &&
                !displayTitle &&
                hasMultimediaTextOrButton:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_FLUSH
                        : ACK_TOP_PADDING_WIDE_FLUSH;
                    return `${top}px 0px 20px 0px`;
                }
                return '0px 0px 20px 0px';
            // 3) title + media, no text/button
            case ContentTypeID === ContainerTypes.Multimedia &&
                hasMultimediaMedia &&
                displayTitle &&
                !hasMultimediaTextOrButton:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 0px 0px 0px`;
                }
                return '20px 0px 0px 0px';
            // 4) title + media + text/button
            case ContentTypeID === ContainerTypes.Multimedia &&
                hasMultimediaMedia &&
                displayTitle &&
                hasMultimediaTextOrButton:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 0px 20px 0px`;
                }
                return '20px 0px 20px 0px';
            // Multimedia fallback:
            case ContentTypeID === ContainerTypes.Multimedia:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 20px 20px 20px`;
                }
                return '20px';

            // Child container (Currently deprecated but still supporting older existent versions)
            // title + img + text & button
            case ContentTypeID === ContainerTypes.ChildPortal &&
                displayTitle &&
                data?.childPortalPart?.images !== null &&
                data?.childPortalPart?.images.length > 0:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 0px 0px 20px`;
                }
                return '20px 0px 0px 20px';
            // no title + img + text & button
            case ContentTypeID === ContainerTypes.ChildPortal &&
                !displayTitle &&
                data?.childPortalPart?.images !== null &&
                data?.childPortalPart?.images.length > 0:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_FLUSH
                        : ACK_TOP_PADDING_WIDE_FLUSH;
                    return `${top}px 0px 20px 0px`;
                }
                return '0px 0px 20px 0px';
            // w/ or w/o title + no img + text & button
            case ContentTypeID === ContainerTypes.ChildPortal &&
                (data?.childPortalPart?.images === null ||
                    data?.childPortalPart?.images.length === 0):
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 20px 20px 20px`;
                }
                return '20px';

            // Link container — image with title, no text, no button
            case ContentTypeID === ContainerTypes.Link && isLinkImageWithTitle:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 0px 0px 0px`;
                }
                return '20px 0px 0px 0px';

            // Link container — image only, no title, no text, no button
            case ContentTypeID === ContainerTypes.Link && isImageOnlyLink:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_FLUSH
                        : ACK_TOP_PADDING_WIDE_FLUSH;
                    return `${top}px 0px 0px 0px`;
                }
                return '0px';

            // Link container in "Image" mode. Text and button are mandatory.
            // title + img + text & button
            case ContentTypeID === ContainerTypes.Link &&
                hasLinkMedia &&
                displayTitle:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 0px 20px 0px`;
                }
                return '20px 0px 20px 0px';
            // w/ or w/o title + no img + text & button
            case ContentTypeID === ContainerTypes.Link && !hasLinkMedia:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_SPACED
                        : ACK_TOP_PADDING_WIDE_SPACED;
                    return `${top}px 20px 20px 20px`;
                }
                return '20px';
            // no title + img + text & button
            case ContentTypeID === ContainerTypes.Link &&
                !displayTitle &&
                hasLinkMedia:
                if (AcknowledgmentReq !== 0) {
                    const top = needsAdditionalPadding
                        ? ACK_TOP_PADDING_NARROW_FLUSH
                        : ACK_TOP_PADDING_WIDE_FLUSH;
                    return `${top}px 0px 20px 0px`;
                }
                return '0px 0px 20px 0px';

            // Asset Upload. Acknowledgment is mandatory and enabled. Upload area or Not Found Box are always present.
            // w/ or w/o title + no media + w/ or w/o text + w/ upload area
            case ContentTypeID === ContainerTypes.AssetUpload &&
                !hasAssetUploadMedia: {
                const top = ACK_TOP_PADDING_WIDE_SPACED;
                return `${top}px 20px 20px 20px`;
            }
            // no title + w/ media + w/ or w/o text + w/ upload area
            case ContentTypeID === ContainerTypes.AssetUpload &&
                hasAssetUploadMedia &&
                !displayTitle: {
                const top = ACK_TOP_PADDING_WIDE_FLUSH;
                return `${top}px 0px 20px 0px`;
            }
            // w/ title + w/ media + w/ or w/o text + w/ upload area
            case ContentTypeID === ContainerTypes.AssetUpload &&
                hasAssetUploadMedia: {
                const top = ACK_TOP_PADDING_WIDE_SPACED;
                return `${top}px 0px 20px 0px`;
            }

            default:
                return '20px';
        }
    };

    const displayContainerTypeName = () => {
        switch (ContentTypeID) {
            case ContainerTypes.Video:
            case ContainerTypes.Text:
            case ContainerTypes.Document:
            case ContainerTypes.Multimedia:
            case ContainerTypes.ChildPortal:
            case ContainerTypes.Link:
            case ContainerTypes.AssetUpload:
            case ContainerTypes.Table:
                return false;
            default:
                return true;
        }
    };

    const displayContainerTitle = () => {
        switch (true) {
            case !displayTitle:
            case ContentTypeID === ContainerTypes.Document:
            case !Subtitle:
                return false;
            default:
                return true;
        }
    };

    const handleAssetUploadComplete = isAssetUploadComplete => {
        setAck(isAssetUploadComplete);
    };

    if (editMode) {
        return (
            <>
                <ContainerContextMenu
                    ref={contextMenuRef} // Assign the ref here
                    isVisible={
                        activeBlock &&
                        activeBlock.temporaryId === block.temporaryId
                    } // Show only for the active block
                    positionX={block.width - 150} // Adjust as needed for positioning
                    handlePropertiesClick={() => handlePropertiesClick(block)}
                    handleDeleteClick={() => handleDeleteClick(block)}
                />
                <div
                    className="ing-card border-light card-width"
                    style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                        backgroundColor: backgroundColor,
                        justifyContent:
                            isChildPortalWithIcon ||
                            block.type === ContainerTypes.Document
                                ? 'center'
                                : 'start',
                    }}
                >
                    {!suppressAcknowledgment && AcknowledgmentReq !== 0 && (
                        <div className="acknowledgment-container">
                            <div
                                className={
                                    'acknowledgment-symbol ' +
                                    (ack
                                        ? 'acknowledged-symbol'
                                        : 'unacknowledged-symbol')
                                }
                            >
                                {ack ? (
                                    <div className="autoMargin">
                                        <i className="bi bi-check2" />
                                    </div>
                                ) : (
                                    <div className="autoMargin">!</div>
                                )}
                            </div>
                            <div
                                className={
                                    'acknowledgment-text-container ' +
                                    (ack
                                        ? 'acknowledged-text-container'
                                        : 'unacknowledged-text-container')
                                }
                            >
                                <div className="acknowledgment-header">
                                    {ack
                                        ? acknowledgedHeaderText
                                        : unacknowledgedHeaderText}
                                </div>
                            </div>
                        </div>
                    )}
                    {!suppressAcknowledgment &&
                        block.type === ContainerTypes.AssetUpload && (
                            <div className="acknowledgment-container">
                                <div
                                    className={
                                        'acknowledgment-symbol ' +
                                        (ack
                                            ? 'acknowledged-symbol'
                                            : 'unacknowledged-symbol')
                                    }
                                >
                                    {ack ? (
                                        <div className="autoMargin">
                                            <i className="bi bi-check2" />
                                        </div>
                                    ) : (
                                        <div className="autoMargin">!</div>
                                    )}
                                </div>
                                <div
                                    className={
                                        'acknowledgment-text-container ' +
                                        (ack
                                            ? 'acknowledged-text-container'
                                            : 'unacknowledged-text-container')
                                    }
                                >
                                    <div className="acknowledgment-header">
                                        {ack && assetUploadCompleteHeaderText}
                                        {!ack &&
                                            daysSinceOrToDueDate <= 0 &&
                                            assetUploadIncompleteHeaderText}
                                        {!ack &&
                                            daysSinceOrToDueDate > 0 &&
                                            assetUploadPastDueHeaderText}
                                    </div>
                                </div>
                            </div>
                        )}
                    <div
                        className="float-end"
                        style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            zIndex: 11,
                            width: 'fit-content',
                            height: 'fit-content',
                            overflow: 'hidden',
                        }}
                    >
                        {!isMobile && (
                            <div
                                onClick={() => toggleContextMenu(block)} // Reuse your existing method
                                style={{
                                    width: '37px',
                                    height: '37px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: '#F4F7FE',
                                    border: '0.5px solid #2F2B43',
                                    borderRadius: '10px',
                                    flexShrink: 0,
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_134_4264)">
                                        <path
                                            d="M6 10C4.9 10 4 10.9 4 12C4 13.1 4.9 14 6 14C7.1 14 8 13.1 8 12C8 10.9 7.1 10 6 10ZM18 10C16.9 10 16 10.9 16 12C16 13.1 16.9 14 18 14C19.1 14 20 13.1 20 12C20 10.9 19.1 10 18 10ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10Z"
                                            fill="#2F2B43"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_134_4264">
                                            <rect
                                                width="24"
                                                height="24"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                        )}
                    </div>
                    <div
                        ref={el =>
                            (contentDivRefs.current[block.temporaryId] = el)
                        }
                        onMouseEnter={e => {
                            if (!isMediaOnlyContent) {
                                e.currentTarget.style.overflowY = 'visible';
                            }
                        }}
                        onMouseLeave={e => {
                            if (!isMediaOnlyContent) {
                                e.currentTarget.style.overflowY = 'hidden';
                            }
                        }}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: getPaddingContainer(),
                            gap: isMediaOnlyContent ? '0px' : '20px',
                            overflow: 'hidden',
                            height:
                                isMediaOnlyContent ||
                                isLinkImageWithTitle ||
                                isMultimediaMediaWithTitle
                                    ? '100%'
                                    : 'fit-content',
                        }}
                    >
                        <p
                            className="card-title"
                            style={{
                                fontSize: '1.25em',
                                color: '#2F2B43',
                                fontWeight: '600',
                                display:
                                    block.type === ContainerTypes.Document ||
                                    block.type === ContainerTypes.Multimedia ||
                                    block.type === ContainerTypes.ChildPortal ||
                                    block.type === ContainerTypes.Link ||
                                    block.type === ContainerTypes.Table ||
                                    block.type === ContainerTypes.AssetUpload
                                        ? 'none'
                                        : 'block',
                            }}
                        >
                            {block.name}
                        </p>
                        {!isChildPortalWithIcon &&
                        block.displayTitle &&
                        block.type !== ContainerTypes.Document &&
                        block.title ? (
                            <div
                                data-container-title="true"
                                style={{
                                    margin: shouldSubtitleHaveMargin
                                        ? '0px 20px'
                                        : '0px',
                                }}
                            >
                                <p className="chart-subtitle">{block.title}</p>
                            </div>
                        ) : null}
                        <div
                            style={{
                                flexGrow: 1,
                                display:
                                    isChildPortalWithIcon ||
                                    block.type === ContainerTypes.Document ||
                                    isImageFillContent
                                        ? 'flex'
                                        : 'initial',
                                height: isMediaOnlyContent ? '100%' : 'auto',
                                overflow: isImageFillContent
                                    ? 'hidden'
                                    : 'visible',
                            }}
                        >
                            <ContentType
                                ContentTypeID={block.type}
                                data={block}
                                editMode={true}
                                handleSetBackgroundColor={() => null}
                                portalName={portalName}
                                portalId={portalId}
                                navigationStack={navigationStack}
                                isMobile={isMobile}
                                onShowPortalView={onShowPortalView}
                            />
                        </div>
                        {HelpBoxText ? (
                            <div
                                className="ellipsis"
                                style={{
                                    marginLeft:
                                        (block.type ===
                                            ContainerTypes.Multimedia &&
                                            ((Array.isArray(
                                                block?.multimediaPart?.images,
                                            ) &&
                                                block.multimediaPart.images
                                                    .length > 0) ||
                                                (block?.multimediaPart?.video &&
                                                    block?.multimediaPart
                                                        ?.video !== ''))) ||
                                        (block.type === ContainerTypes.Link &&
                                            block?.linkPart?.image !== null &&
                                            block?.linkPart?.image !==
                                                undefined) ||
                                        (block.type ===
                                            ContainerTypes.AssetUpload &&
                                            Array.isArray(
                                                block?.containerAssets,
                                            ) &&
                                            block.containerAssets.length > 0)
                                            ? '20px'
                                            : '0px',
                                }}
                            >
                                <i className="bi bi-info-circle" />
                            </div>
                        ) : null}
                    </div>
                </div>
                <div
                    onMouseDown={e => handleResizeMouseDown(e, block)}
                    style={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        width: '14px',
                        height: '14px',
                        cursor: 'nwse-resize',
                        zIndex: 3,
                    }}
                >
                    <svg
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            width: '14px',
                            height: '14px',
                            pointerEvents: 'none',
                            opacity: 1,
                            filter: 'drop-shadow(0px 0px 1px rgba(255, 255, 255, 1)) drop-shadow(0px 0px 1px rgba(255, 255, 255, 1)) drop-shadow(0px 0px 0px rgba(255, 255, 255, 1))',
                        }}
                        height="18"
                        viewBox="0 0 18 18"
                        width="18"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="m14.228 16.227a1 1 0 0 1 -.707-1.707l1-1a1 1 0 0 1 1.416 1.414l-1 1a1 1 0 0 1 -.707.293zm-5.638 0a1 1 0 0 1 -.707-1.707l6.638-6.638a1 1 0 0 1 1.416 1.414l-6.638 6.638a1 1 0 0 1 -.707.293zm-5.84 0a1 1 0 0 1 -.707-1.707l12.477-12.477a1 1 0 1 1 1.415 1.414l-12.478 12.477a1 1 0 0 1 -.707.293z"
                            fill="rgba(102, 112, 133, 1)"
                        />
                    </svg>
                </div>
            </>
        );
    } else {
        return (
            <div
                className={
                    ContentTypeID === ContainerTypes.Table
                        ? 'ing-card2 d-flex card-width '
                        : 'ing-card d-flex card-width '
                }
                style={{
                    width: isMobile ? '100%' : width,
                    maxWidth: '100%',
                    height: isMobile ? '100%' : height,
                    backgroundColor:
                        ContentTypeID === ContainerTypes.Table
                            ? isMobile
                                ? '#f7faf7'
                                : '#ffffff'
                            : ContentTypeID === ContainerTypes.ChildPortal ||
                                ContentTypeID === ContainerTypes.Link
                              ? backgroundColor
                              : '',
                    boxShadow:
                        ContentTypeID === ContainerTypes.Table ? 'none' : '',
                    border:
                        ContentTypeID === ContainerTypes.Table ? 'none' : '',
                    cursor:
                        ContentTypeID === ContainerTypes.ChildPortal ||
                        ContentTypeID === ContainerTypes.Link
                            ? 'pointer'
                            : 'auto',
                }}
            >
                {!suppressAcknowledgment && AcknowledgmentReq !== 0 && (
                    <div className="acknowledgment-container">
                        <div
                            className={
                                'acknowledgment-symbol ' +
                                (ack
                                    ? 'acknowledged-symbol'
                                    : 'unacknowledged-symbol')
                            }
                        >
                            {ack ? (
                                <div className="autoMargin">
                                    <i className="bi bi-check2" />
                                </div>
                            ) : (
                                <div className="autoMargin">!</div>
                            )}
                        </div>
                        <div
                            className={
                                'acknowledgment-text-container ' +
                                (ack
                                    ? 'acknowledged-text-container'
                                    : 'unacknowledged-text-container')
                            }
                        >
                            <div className="acknowledgment-header">
                                {ack
                                    ? acknowledgedHeaderText
                                    : unacknowledgedHeaderText}
                                <i
                                    className={
                                        'pointer-cursor float-right bi bi-chevron-' +
                                        (expandHelpBox ? 'up' : 'down')
                                    }
                                    onClick={() =>
                                        setExpandHelpBox(!expandHelpBox)
                                    }
                                />
                            </div>
                            {expandHelpBox && (
                                <>
                                    <div className="acknowledgment-text">
                                        {AcknowledgmentText}
                                    </div>
                                    <div>
                                        <div className="acknowledgment-checkbox-container">
                                            <input
                                                type="checkbox"
                                                checked={ack}
                                                onChange={e =>
                                                    onAckChange(
                                                        e.target.checked,
                                                    )
                                                }
                                                className="form-check-input custom-checkbox"
                                            />
                                            {getText('msg_258cbb61dd16')}
                                            <span className="requiredAfter">
                                                *
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
                {!suppressAcknowledgment &&
                    ContentTypeID === ContainerTypes.AssetUpload && (
                        <div className="acknowledgment-container">
                            <div
                                className={
                                    'acknowledgment-symbol ' +
                                    (ack
                                        ? 'acknowledged-symbol'
                                        : 'unacknowledged-symbol')
                                }
                            >
                                {ack ? (
                                    <div className="autoMargin">
                                        <i className="bi bi-check2" />
                                    </div>
                                ) : (
                                    <div className="autoMargin">!</div>
                                )}
                            </div>
                            <div
                                className={
                                    'acknowledgment-text-container ' +
                                    (ack
                                        ? 'acknowledged-text-container'
                                        : 'unacknowledged-text-container')
                                }
                            >
                                <div className="acknowledgment-header">
                                    {ack && assetUploadCompleteHeaderText}
                                    {!ack &&
                                        daysSinceOrToDueDate <= 0 &&
                                        assetUploadIncompleteHeaderText}
                                    {!ack &&
                                        daysSinceOrToDueDate > 0 &&
                                        assetUploadPastDueHeaderText}
                                </div>
                            </div>
                        </div>
                    )}
                <div
                    style={{
                        borderRadius: '10px',
                        height: '100%',
                        overflowY: isMediaOnlyContent ? 'hidden' : 'auto',
                        overflowX: 'hidden',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: getPaddingContainer(),
                        gap: isMediaOnlyContent ? '0px' : '20px',
                    }}
                    onClick={() =>
                        isChildPortalWithIcon || isLinkContainerWithImage
                            ? handleContainerClick()
                            : {}
                    }
                    onClickCapture={e => {
                        if (handleSSOLoginFailedLinkClick(e)) {
                            return;
                        }

                        if (!(editMode || isPreview || isViewLive)) {
                            return;
                        }

                        const anchor = e.target.closest(
                            'a[href="#sso-blocked"]',
                        );
                        if (anchor) {
                            e.preventDefault();
                            e.stopPropagation();
                            Toast.info(
                                getText('msg_29393dec2c1f'),
                                {},
                                getText('msg_6c8145e3a0f9'),
                            );
                        }
                    }}
                >
                    <p
                        className="chart-title card-title"
                        style={{
                            display: displayContainerTypeName()
                                ? 'block'
                                : 'none',
                        }}
                    >
                        {Name}
                    </p>
                    {!isChildPortalWithIcon ? (
                        <div
                            style={{
                                marginLeft: shouldSubtitleHaveMargin
                                    ? '20px'
                                    : '0px',
                                marginRight: shouldSubtitleHaveMargin
                                    ? '20px'
                                    : '0px',
                                display:
                                    ContentTypeID === ContainerTypes.Table &&
                                    isMobileView
                                        ? 'block'
                                        : displayContainerTitle()
                                          ? 'block'
                                          : 'none',
                            }}
                        >
                            <p
                                className={
                                    (ContentTypeID ===
                                        ContainerTypes.ChildPortal ||
                                        ContentTypeID ===
                                            ContainerTypes.Link) &&
                                    !isChildPortalWithImage
                                        ? 'chart-subtitle mb-0'
                                        : 'chart-subtitle'
                                }
                            >
                                {Subtitle}
                            </p>
                        </div>
                    ) : null}
                    <div
                        style={{
                            flexGrow: 1,
                            display:
                                isChildPortalWithIcon || isImageFillContent
                                    ? 'flex'
                                    : 'initial',
                            height: isMediaOnlyContent ? '100%' : 'auto',
                            overflow: isImageFillContent ? 'hidden' : 'visible',
                        }}
                    >
                        <ContentType
                            name="Content Type 2"
                            ContentTypeID={ContentTypeID}
                            data={data}
                            editMode={editMode}
                            handleSetBackgroundColor={handleSetBackgroundColor}
                            onAssetUploadComplete={handleAssetUploadComplete}
                            onLinkClick={handleContainerClick}
                            portalName={portalName}
                            portalId={portalId}
                            navigationStack={navigationStack}
                            isPreview={isPreview}
                            isViewLive={isViewLive}
                            isMobile={isMobile}
                            onShowPortalView={onShowPortalView}
                        />
                    </div>
                    {/* Next code block will keep "i" icon pinned to bottom of container, overlapping with content if there's scroll */}
                    {/*
                    <div className="ellipsis" style={{ position: "absolute", bottom:"24px", left:"24px", display: HelpBoxText ? "block" : "none"}}>
                        <i className="bi bi-info-circle pointer-cursor" onClick={() => setShowHelpBox(!showHelpBox)} style={{backgroundColor:"white", borderRadius:"100px"}}/>
                    </div>
                    */}
                    <div
                        className="ellipsis"
                        style={{
                            display: HelpBoxText ? 'block' : 'none',
                            marginLeft: shouldSubtitleHaveMargin
                                ? '20px'
                                : '0px',
                        }}
                    >
                        <i
                            className="bi bi-info-circle pointer-cursor"
                            onClick={() => setShowHelpBox(!showHelpBox)}
                        />
                    </div>
                </div>
                {showHelpBox && (
                    <div className="help-box shadow-sm">
                        <div>{HelpBoxText}</div>
                    </div>
                )}
            </div>
        );
    }
}
